
import React, { useRef, useState, useEffect } from "react";
import { toast } from "sonner";

interface CameraSetupProps {
  mainVideoRef: React.RefObject<HTMLVideoElement>;
  pipVideoRef: React.RefObject<HTMLVideoElement>;
  onStreamsReady: (mainStream: MediaStream, pipStream: MediaStream) => void;
  onReadyToRecord: () => void;
}

const CameraSetup: React.FC<CameraSetupProps> = ({
  mainVideoRef,
  pipVideoRef,
  onStreamsReady,
  onReadyToRecord
}) => {
  const [setupAttempts, setSetupAttempts] = useState(0);
  const maxSetupAttempts = 5;
  const mainStreamRef = useRef<MediaStream | null>(null);
  const pipStreamRef = useRef<MediaStream | null>(null);
  const setupTimeoutRef = useRef<number | null>(null);
  
  useEffect(() => {
    console.log("CameraSetup mounted, initializing cameras");
    setupCameras();
    
    // Cleanup function
    return () => {
      console.log("CameraSetup unmounting, cleaning up streams");
      stopStreams();
      if (setupTimeoutRef.current) {
        clearTimeout(setupTimeoutRef.current);
      }
    };
  }, [setupAttempts]);
  
  const stopStreams = () => {
    // Stop any active media tracks
    if (mainStreamRef.current) {
      mainStreamRef.current.getTracks().forEach(track => track.stop());
      mainStreamRef.current = null;
    }
    
    if (pipStreamRef.current) {
      pipStreamRef.current.getTracks().forEach(track => track.stop());
      pipStreamRef.current = null;
    }
  };
  
  const setupCameras = async () => {
    console.log(`Setting up cameras (attempt ${setupAttempts + 1})...`);
    
    try {
      // Clean up any existing streams first
      stopStreams();
      
      // Create a cache-busting string to prevent browsers from reusing old stream objects
      const cacheBuster = new Date().getTime().toString();
      
      // Try to get user media with less strict constraints first
      console.log("Requesting camera and audio permissions...");
      
      let backStream: MediaStream;
      let frontStream: MediaStream;
      let audioStream: MediaStream;
      
      // First get audio separately - more reliable
      try {
        audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        console.log("Audio stream obtained:", audioStream.getAudioTracks().length, "tracks");
      } catch (error) {
        console.warn("Could not access microphone:", error);
        toast.warning("Microphone not accessible. Recording will have no audio.");
        audioStream = new MediaStream(); // Empty stream as fallback
      }
      
      // Try to get main camera with multiple fallback options
      try {
        console.log("Attempting to access main camera with ideal settings...");
        // For mobile devices, try environment facing camera
        backStream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
        
        console.log("Main camera obtained:", 
          backStream.getVideoTracks().length, "tracks", 
          backStream.getVideoTracks()[0]?.label);
      } catch (backError) {
        console.warn("Could not access main camera with ideal settings:", backError);
        
        try {
          console.log("Attempting to access main camera with minimal settings...");
          backStream = await navigator.mediaDevices.getUserMedia({
            video: true
          });
          console.log("Generic camera for main view obtained");
        } catch (genericError) {
          console.error("Could not access any camera for main view:", genericError);
          toast.error("Camera access failed. Please check your browser permissions.");
          throw new Error("Camera access failed completely");
        }
      }
      
      // Try to get front (user) camera with multiple fallback options
      try {
        console.log("Attempting to access front camera with ideal settings...");
        frontStream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: "user",
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });
        console.log("Front camera obtained:", frontStream.getVideoTracks().length, "tracks", 
          frontStream.getVideoTracks()[0]?.label);
      } catch (frontError) {
        console.warn("Could not access front camera with ideal settings:", frontError);
        
        // If we already have a main camera, use it for both views
        if (backStream.getVideoTracks().length > 0) {
          console.log("Using main camera for PIP view");
          frontStream = new MediaStream();
          const mainTrack = backStream.getVideoTracks()[0];
          if (mainTrack) {
            frontStream.addTrack(mainTrack.clone());
          } else {
            toast.error("No video tracks available");
            throw new Error("No video tracks available");
          }
        } else {
          // Last resort, try any camera
          try {
            console.log("Attempting to access any camera for front view...");
            frontStream = await navigator.mediaDevices.getUserMedia({
              video: true
            });
            console.log("Generic camera for front view obtained");
          } catch (genericError) {
            console.error("Could not access any camera for front view:", genericError);
            toast.error("Front camera access failed. Facial emotions won't be detected.");
            frontStream = new MediaStream(); // Empty stream as fallback
          }
        }
      }
      
      // Add audio tracks to streams
      if (audioStream.getAudioTracks().length > 0) {
        const audioTrack = audioStream.getAudioTracks()[0];
        if (backStream && !backStream.getAudioTracks().length) {
          console.log("Adding audio track to main stream");
          backStream.addTrack(audioTrack.clone());
        }
        
        if (frontStream && !frontStream.getAudioTracks().length) {
          console.log("Adding audio track to front stream");
          frontStream.addTrack(audioTrack);
        }
      }
      
      // For single camera devices, ensure we have video in both streams
      if (!backStream.getVideoTracks().length && frontStream.getVideoTracks().length > 0) {
        console.log("No main camera available, using front camera for both views");
        const frontTrack = frontStream.getVideoTracks()[0];
        if (frontTrack) {
          backStream.addTrack(frontTrack.clone());
        }
      }
      
      if (!frontStream.getVideoTracks().length && backStream.getVideoTracks().length > 0) {
        console.log("No front camera available, using main camera for both views");
        const backTrack = backStream.getVideoTracks()[0];
        if (backTrack) {
          frontStream.addTrack(backTrack.clone());
        }
      }
      
      // If we still don't have video tracks, show an error
      if (!backStream.getVideoTracks().length && !frontStream.getVideoTracks().length) {
        console.error("No cameras available");
        toast.error("No cameras detected or permissions denied");
        throw new Error("No cameras available");
      }
      
      // Save references to the streams
      mainStreamRef.current = backStream;
      pipStreamRef.current = frontStream;
      
      // Apply constraints to improve video quality
      try {
        if (backStream.getVideoTracks().length > 0) {
          await backStream.getVideoTracks()[0].applyConstraints({
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30, min: 15 }
          });
        }
        
        if (frontStream.getVideoTracks().length > 0) {
          await frontStream.getVideoTracks()[0].applyConstraints({
            width: { ideal: 640 },
            height: { ideal: 480 },
            frameRate: { ideal: 30, min: 15 }
          });
        }
      } catch (e) {
        console.warn("Could not apply constraints to video tracks:", e);
      }
      
      // Attach streams to video elements
      if (mainVideoRef.current) {
        console.log("Attaching main stream to video element");
        mainVideoRef.current.srcObject = backStream;
        mainVideoRef.current.muted = true;
        
        // Force play and handle errors
        const playMainVideo = async () => {
          try {
            // Add cache-busting query parameter
            if (mainVideoRef.current) {
              await mainVideoRef.current.play();
              console.log("Main video playing");
            }
          } catch (e) {
            console.error("Error playing main video:", e);
            // Add click-to-play fallback
            document.addEventListener('click', () => {
              mainVideoRef.current?.play().catch(e => 
                console.error("Second attempt to play main video failed:", e)
              );
            }, { once: true });
          }
        };
        
        // Play video when metadata is loaded
        if (mainVideoRef.current.readyState >= 2) {
          // Metadata already loaded
          playMainVideo();
        } else {
          // Wait for metadata to load
          mainVideoRef.current.onloadedmetadata = () => playMainVideo();
        }
      }
      
      if (pipVideoRef.current) {
        console.log("Attaching PIP stream to video element");
        pipVideoRef.current.srcObject = frontStream;
        pipVideoRef.current.muted = true;
        
        // Force play and handle errors
        const playPipVideo = async () => {
          try {
            if (pipVideoRef.current) {
              await pipVideoRef.current.play();
              console.log("PIP video playing");
            }
          } catch (e) {
            console.error("Error playing PIP video:", e);
            // Add click-to-play fallback
            document.addEventListener('click', () => {
              pipVideoRef.current?.play().catch(e => 
                console.error("Second attempt to play PIP video failed:", e)
              );
            }, { once: true });
          }
        };
        
        // Play video when metadata is loaded
        if (pipVideoRef.current.readyState >= 2) {
          // Metadata already loaded
          playPipVideo();
        } else {
          // Wait for metadata to load
          pipVideoRef.current.onloadedmetadata = () => playPipVideo();
        }
      }
      
      // Signal that streams are ready
      onStreamsReady(backStream, frontStream);
      
      // Brief timeout to allow videos to initialize fully before signaling ready
      if (setupTimeoutRef.current) {
        clearTimeout(setupTimeoutRef.current);
      }
      
      setupTimeoutRef.current = window.setTimeout(() => {
        console.log("Camera setup complete. Signaling ready to record");
        
        // Final video status check
        if (mainVideoRef.current && pipVideoRef.current) {
          console.log("Final video status check:", 
            "Main:", mainVideoRef.current.readyState, mainVideoRef.current.videoWidth, 
            "PIP:", pipVideoRef.current.readyState, pipVideoRef.current.videoWidth);
            
          // If videos are not ready yet, show a toast to prompt user interaction
          if (!mainVideoRef.current.videoWidth || !pipVideoRef.current.videoWidth) {
            console.warn("Video dimensions not available yet, may need user interaction");
            toast.info("Tap the screen to activate cameras", { duration: 5000 });
          }
        }
        
        // Signal ready to record even if dimensions are not available yet
        onReadyToRecord();
        toast.success("Cameras ready. Click the record button to start recording.");
      }, 2000); // Increased timeout to give more time for video initialization
    } catch (error) {
      console.error("Camera setup failed:", error);
      
      if (setupAttempts < maxSetupAttempts) {
        toast.error(`Camera setup failed (attempt ${setupAttempts + 1}/${maxSetupAttempts}). Retrying...`);
        setSetupAttempts(prev => prev + 1);
      } else {
        toast.error("Could not initialize cameras after multiple attempts. Please check permissions and refresh the page.");
      }
    }
  };

  return null; // This component doesn't render anything
};

export default CameraSetup;
